<template>
  <div class="admin-panel task-page task-page--cosec">

    <admin-header
      variant="back"
      :crumbs="[{ label: 'Client Management', to: { name: 'Admin-Panel', params: { lang: $route.params.lang } } }, { label: 'Cosec & Business Support Tasks' }]"
      :back-to="{ name: 'Admin-Panel', params: { lang: $route.params.lang } }"
      @open-logout="openLogoutModal"
    />

    <main class="ap-container ap-fade-in">

      <!-- Page hero -->
      <div class="tp-hero">
        <div class="tp-hero-icon">
          <fa-icon :icon="['fas', 'stamp']" />
        </div>
        <div>
          <h1 class="tp-hero-title">Cosec &amp; Business Support</h1>
          <p class="tp-hero-sub">Manage company secretarial compliance, payroll, and accounting task deadlines.</p>
        </div>
      </div>

      <!-- Ad hoc notification strip -->
      <div class="tp-adhoc-strip" :class="{ 'is-open': adhocStripOpen, 'has-items': adhocStripItems.length }">
        <div class="tp-adhoc-strip-toggle" @click="adhocStripOpen = !adhocStripOpen">
          <span class="tp-adhoc-strip-label">
            <fa-icon :icon="['fas', 'calendar-times']" class="tp-adhoc-strip-icon" />
            <template v-if="viewMode === 'calendar'">
              Ad Hoc Deadlines — <strong>{{ adhocStripItems.length }}</strong> in {{ monthNames[calendarMonth - 1] }}
            </template>
            <template v-else>
              Ad Hoc Deadlines — <strong>{{ adhocStripItems.length }}</strong> total this year
            </template>
          </span>
          <div class="tp-adhoc-strip-actions">
            <button class="tp-adhoc-add-btn" @click.stop="showAdHocForm = true">
              <fa-icon :icon="['fas', 'plus']" /> Add
            </button>
            <fa-icon :icon="['fas', adhocStripOpen ? 'chevron-up' : 'chevron-down']" class="tp-adhoc-strip-caret" />
          </div>
        </div>
        <transition name="tp-adhoc-collapse">
          <div v-if="adhocStripOpen" class="tp-adhoc-strip-body">
            <!-- Calendar view: flat list for the viewed month -->
            <template v-if="viewMode === 'calendar'">
              <div v-if="!adhocStripItems.length" class="tp-adhoc-strip-empty">
                No ad hoc deadlines in {{ monthNames[calendarMonth - 1] }}.
              </div>
              <div v-else class="tp-adhoc-strip-list">
                <div v-for="item in adhocStripItems" :key="item.adhocId" class="tp-adhoc-strip-item">
                  <span class="tp-adhoc-strip-item-chip">
                    <span class="tp-adhoc-strip-item-initial" :title="item.clientName">{{ item.initial }}</span>
                    <span class="tp-adhoc-strip-item-task">{{ item.display_name }}</span>
                    <span class="tp-adhoc-strip-item-day">{{ item.date.day }}/{{ item.date.month }}</span>
                  </span>
                  <button class="tp-adhoc-strip-delete" title="Delete" @click="confirmDeleteAdhoc(item)">
                    <fa-icon :icon="['fas', 'trash']" />
                  </button>
                </div>
              </div>
            </template>
            <!-- Table view: all months grouped -->
            <template v-else>
              <div v-if="!adhocStripGroups.length" class="tp-adhoc-strip-empty">
                No ad hoc deadlines this year.
              </div>
              <template v-else>
                <div v-for="group in adhocStripGroups" :key="group.month" class="tp-adhoc-strip-group">
                  <div class="tp-adhoc-strip-group-month">{{ group.monthName }}</div>
                  <div class="tp-adhoc-strip-list">
                    <div v-for="item in group.items" :key="item.adhocId" class="tp-adhoc-strip-item">
                      <span class="tp-adhoc-strip-item-chip">
                        <span class="tp-adhoc-strip-item-initial" :title="item.clientName">{{ item.initial }}</span>
                        <span class="tp-adhoc-strip-item-task">{{ item.display_name }}</span>
                        <span class="tp-adhoc-strip-item-day">{{ item.date.day }}/{{ item.date.month }}</span>
                      </span>
                      <button class="tp-adhoc-strip-delete" title="Delete" @click="confirmDeleteAdhoc(item)">
                        <fa-icon :icon="['fas', 'trash']" />
                      </button>
                    </div>
                  </div>
                </div>
              </template>
            </template>
          </div>
        </transition>
      </div>

      <!-- Summary strip -->
      <div class="tp-summary">
        <div class="tp-summary-card">
          <span class="tp-summary-val">{{ cosecClients.length }}</span>
          <span class="tp-summary-label">Clients</span>
        </div>
        <div class="tp-summary-card">
          <span class="tp-summary-val">{{ currentMonthDeadlines }}</span>
          <span class="tp-summary-label">Due This Month</span>
        </div>
        <div class="tp-summary-card">
          <span class="tp-summary-val">{{ nextMonthDeadlines }}</span>
          <span class="tp-summary-label">Due Next Month</span>
        </div>
        <div class="tp-summary-card">
          <span class="tp-summary-val">{{ totalDeadlines }}</span>
          <span class="tp-summary-label">Total Deadlines</span>
        </div>
      </div>

      <!-- Calendar Table Panel -->
      <div class="tp-panel">
        <div class="tp-panel-head">
          <h3>Cosec &amp; Business Support Calendar</h3>
          <div class="tp-panel-head-right">
            <div ref="yearTabs" class="tp-year-tabs">
              <span class="tp-year-pill" :style="pillStyle" />
              <button
                v-for="opt in slotOptions"
                :key="opt.slot"
                class="tp-year-tab"
                :class="{ 'is-active': selectedSlot === opt.slot }"
                @click="selectedSlot = opt.slot"
              >{{ opt.year }}</button>
            </div>
            <app-divider color="#d1d5db" height="20px"></app-divider>
            <!-- View-mode toggle: table / calendar -->
            <div class="tp-view-toggle">
              <button
                class="tp-view-toggle-btn"
                :class="{ 'is-active': viewMode === 'table' }"
                title="Table View"
                @click="viewMode = 'table'"
              >
                <fa-icon :icon="['fas', 'table']" />
              </button>
              <button
                class="tp-view-toggle-btn"
                :class="{ 'is-active': viewMode === 'calendar' }"
                title="Calendar View"
                @click="viewMode = 'calendar'"
              >
                <fa-icon :icon="['fas', 'calendar-alt']" />
              </button>
            </div>
            <div v-if="rolloverNeeded">
              <app-divider color="#d1d5db" height="20px"></app-divider>
              <app-button size="sm" variant="danger" :bold="true" @click="showRolloverConfirm = true">
                <fa-icon :icon="['fas', 'undo-alt']" />
                Year Rollover
              </app-button>
            </div>
            <app-divider color="#d1d5db" height="20px"></app-divider>
            <app-badge v-if="!loading" size="xl" :caps="false" variant="custom" custom-color="#374151" custom-bg="#e5e7eb" :inner-shadow="true">
              <span>{{ cosecClients.length }} client{{ cosecClients.length !== 1 ? 's' : '' }}</span></app-badge>
          </div>
        </div>

        <!-- Column visibility toolbar — table mode only -->
        <div v-if="viewMode === 'table' && !loading && cosecClients.length" class="tp-col-toolbar">
          <button
            class="ap-filter-chip ap-filter-chip-cat ap-filter-chip-cat--cosec"
            :class="{ active: !hiddenMetaGroups.includes('info') }"
            :title="(hiddenMetaGroups.includes('info') ? 'Show' : 'Hide') + ' Initial & Type columns'"
            @click="toggleMetaGroup('info')"
          >Info</button>
          <button
            class="ap-filter-chip ap-filter-chip-cat ap-filter-chip-cat--cosec"
            :class="{ active: !hiddenMetaGroups.includes('service') }"
            :title="(hiddenMetaGroups.includes('service') ? 'Show' : 'Hide') + ' Service column'"
            @click="toggleMetaGroup('service')"
          >Service</button>
          <button
            class="ap-filter-chip ap-filter-chip-cat ap-filter-chip-cat--cosec"
            :class="{ active: !hiddenMetaGroups.includes('ssm') }"
            :title="(hiddenMetaGroups.includes('ssm') ? 'Show' : 'Hide') + ' SSM columns'"
            @click="toggleMetaGroup('ssm')"
          >SSM</button>
          <button
            class="ap-filter-chip ap-filter-chip-cat ap-filter-chip-cat--cosec"
            :class="{ active: !hiddenMetaGroups.includes('tax_info') }"
            :title="(hiddenMetaGroups.includes('tax_info') ? 'Show' : 'Hide') + ' Tax info columns'"
            @click="toggleMetaGroup('tax_info')"
          >Tax Info</button>
          <button
            class="ap-filter-chip ap-filter-chip-cat ap-filter-chip-cat--cosec"
            :class="{ active: !hiddenMetaGroups.includes('dates') }"
            :title="(hiddenMetaGroups.includes('dates') ? 'Show' : 'Hide') + ' Dates columns'"
            @click="toggleMetaGroup('dates')"
          >Dates</button>
        </div>

        <div v-if="loading" class="tp-empty">
          <fa-icon :icon="['fas', 'spinner']" spin class="tp-empty-icon" />
          <p>Loading cosec clients…</p>
        </div>

        <div v-else-if="!cosecClients.length" class="tp-empty">
          <fa-icon :icon="['fas', 'stamp']" class="tp-empty-icon" />
          <p>No cosec clients found. Add clients with Cosec, Payroll, or Accounting services to see their deadlines here.</p>
        </div>

        <!-- Calendar view -->
        <task-calendar-view
          v-else-if="viewMode === 'calendar'"
          :clients="cosecClients"
          :calendar-month="calendarMonth"
          :calendar-year="calendarYear"
          :current-month="currentMonth"
          :current-year="currentYear"
          :selected-slot="selectedSlot"
          :month-names="monthNames"
          :updating-task="updatingTask"
          :updating-adhoc="updatingAdhoc"
          :get-deadlines="getDeadlines"
          :deadline-class="deadlineClass"
          @update-status="updateTaskStatus"
          @prev-month="prevCalMonth"
          @next-month="nextCalMonth"
          @set-month="setCalMonth"
        />

        <template v-else>
          <div ref="calWrap" class="tp-cal-wrap" :style="{ height: tableHeight }">
            <table class="tp-cal-table">
              <colgroup>
                <col class="tp-col-num">
                <col class="tp-col-company">
                <!-- info: Initial, Type -->
                <col v-if="!hiddenMetaGroups.includes('info')" class="tp-col-initial">
                <col v-if="!hiddenMetaGroups.includes('info')" class="tp-col-meta">
                <!-- service -->
                <col v-if="!hiddenMetaGroups.includes('service')" style="width:160px;min-width:140px">
                <!-- ssm: SSM New, SSM Old -->
                <col v-if="!hiddenMetaGroups.includes('ssm')" class="tp-col-meta">
                <col v-if="!hiddenMetaGroups.includes('ssm')" class="tp-col-meta">
                <!-- tax_info: Tax Grp, Tax No., E Pfx, E No., IRB Branch, Auditor -->
                <col v-if="!hiddenMetaGroups.includes('tax_info')" class="tp-col-meta">
                <col v-if="!hiddenMetaGroups.includes('tax_info')" class="tp-col-meta">
                <col v-if="!hiddenMetaGroups.includes('tax_info')" class="tp-col-meta">
                <col v-if="!hiddenMetaGroups.includes('tax_info')" class="tp-col-meta">
                <col v-if="!hiddenMetaGroups.includes('tax_info')" class="tp-col-meta">
                <col v-if="!hiddenMetaGroups.includes('tax_info')" class="tp-col-meta">
                <!-- dates: Year End, Inc. Date, Term. Date, Term. Type -->
                <col v-if="!hiddenMetaGroups.includes('dates')" class="tp-col-meta">
                <col v-if="!hiddenMetaGroups.includes('dates')" class="tp-col-meta">
                <col v-if="!hiddenMetaGroups.includes('dates')" class="tp-col-meta">
                <col v-if="!hiddenMetaGroups.includes('dates')" class="tp-col-meta">
                <col v-for="m in 12" :key="'mc-' + m" class="tp-col-month">
                <col class="tp-col-actions">
                <col class="tp-col-spacer" :style="{ minWidth: spacerWidth + 'px' }">
              </colgroup>
              <thead>
                <tr>
                  <th class="tp-cal-th tp-cal-sticky tp-cal-th-num" style="left:0">#</th>
                  <th class="tp-cal-th tp-cal-sticky tp-cal-th-company" style="left:50px">Client</th>
                  <th v-if="!hiddenMetaGroups.includes('info')" class="tp-cal-th tp-cal-th-meta">Initial</th>
                  <th v-if="!hiddenMetaGroups.includes('info')" class="tp-cal-th tp-cal-th-meta">Type</th>
                  <th v-if="!hiddenMetaGroups.includes('service')" class="tp-cal-th">Service</th>
                  <th v-if="!hiddenMetaGroups.includes('ssm')" class="tp-cal-th tp-cal-th-meta">SSM (New)</th>
                  <th v-if="!hiddenMetaGroups.includes('ssm')" class="tp-cal-th tp-cal-th-meta">SSM (Old)</th>
                  <th v-if="!hiddenMetaGroups.includes('tax_info')" class="tp-cal-th tp-cal-th-meta">Tax Grp</th>
                  <th v-if="!hiddenMetaGroups.includes('tax_info')" class="tp-cal-th tp-cal-th-meta">Tax No.</th>
                  <th v-if="!hiddenMetaGroups.includes('tax_info')" class="tp-cal-th tp-cal-th-meta">E Pfx</th>
                  <th v-if="!hiddenMetaGroups.includes('tax_info')" class="tp-cal-th tp-cal-th-meta">E No.</th>
                  <th v-if="!hiddenMetaGroups.includes('tax_info')" class="tp-cal-th tp-cal-th-meta">IRB Branch</th>
                  <th v-if="!hiddenMetaGroups.includes('tax_info')" class="tp-cal-th tp-cal-th-meta">Auditor</th>
                  <th v-if="!hiddenMetaGroups.includes('dates')" class="tp-cal-th tp-cal-th-meta">Year End</th>
                  <th v-if="!hiddenMetaGroups.includes('dates')" class="tp-cal-th tp-cal-th-meta">Inc. Date</th>
                  <th v-if="!hiddenMetaGroups.includes('dates')" class="tp-cal-th tp-cal-th-meta">Term. Date</th>
                  <th v-if="!hiddenMetaGroups.includes('dates')" class="tp-cal-th tp-cal-th-meta">Term. Type</th>
                  <th
                    v-for="(name, i) in monthNames"
                    :key="'mh-' + i"
                    class="tp-cal-th tp-cal-th-month"
                    :class="{ 'tp-cal-th-current': i + 1 === currentMonth && selectedSlot === 'current' }"
                  >{{ name }}</th>
                  <th class="tp-cal-th"></th>
                  <th class="tp-col-spacer" :style="{ minWidth: spacerWidth + 'px' }"></th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(client, i) in paginatedClients" :key="client.id" class="tp-cal-row">
                  <td class="tp-cal-td tp-cal-sticky tp-cal-td-num" style="left:0">{{ rowNumber(i) }}</td>
                  <td class="tp-cal-td tp-cal-sticky tp-cal-td-company" style="left:50px">{{ client.client }}</td>
                  <td v-if="!hiddenMetaGroups.includes('info')" class="tp-cal-td tp-cal-td-meta">{{ client.initial || '—' }}</td>
                  <td v-if="!hiddenMetaGroups.includes('info')" class="tp-cal-td tp-cal-td-meta">{{ formatClientType(client.client_type) }}</td>
                  <td v-if="!hiddenMetaGroups.includes('service')" class="tp-cal-td">
                    <div class="tp-svc-tags">
                      <app-badge
                        v-for="svc in client.services"
                        :key="svc.name"
                        :caps="true"
                        size="sm"
                        :services="svc.name"
                      >{{ svc.display_name }}</app-badge>
                    </div>
                  </td>
                  <td v-if="!hiddenMetaGroups.includes('ssm')" class="tp-cal-td tp-cal-td-meta ap-mono" :class="{ 'ap-cell-empty': !client.ssm_number_new }">{{ client.ssm_number_new || '—' }}</td>
                  <td v-if="!hiddenMetaGroups.includes('ssm')" class="tp-cal-td tp-cal-td-meta ap-mono" :class="{ 'ap-cell-empty': !client.ssm_number_old }">{{ client.ssm_number_old || '—' }}</td>
                  <td v-if="!hiddenMetaGroups.includes('tax_info')" class="tp-cal-td tp-cal-td-meta ap-mono" :class="{ 'ap-cell-empty': !(client.tax_number && client.tax_number.group) }">{{ (client.tax_number && client.tax_number.group) || '—' }}</td>
                  <td v-if="!hiddenMetaGroups.includes('tax_info')" class="tp-cal-td tp-cal-td-meta ap-mono" :class="{ 'ap-cell-empty': !(client.tax_number && client.tax_number.number) }">{{ (client.tax_number && client.tax_number.number) || '—' }}</td>
                  <td v-if="!hiddenMetaGroups.includes('tax_info')" class="tp-cal-td tp-cal-td-meta ap-mono" :class="{ 'ap-cell-empty': !(client.e_number && client.e_number.prefixed) }">{{ (client.e_number && client.e_number.prefixed) || '—' }}</td>
                  <td v-if="!hiddenMetaGroups.includes('tax_info')" class="tp-cal-td tp-cal-td-meta ap-mono" :class="{ 'ap-cell-empty': !(client.e_number && client.e_number.number) }">{{ (client.e_number && client.e_number.number) || '—' }}</td>
                  <td v-if="!hiddenMetaGroups.includes('tax_info')" class="tp-cal-td tp-cal-td-meta" :class="{ 'ap-cell-empty': !client.irb_branch }">{{ client.irb_branch || '—' }}</td>
                  <td v-if="!hiddenMetaGroups.includes('tax_info')" class="tp-cal-td tp-cal-td-meta" :class="{ 'ap-cell-empty': !client.auditor }">{{ client.auditor || '—' }}</td>
                  <td v-if="!hiddenMetaGroups.includes('dates')" class="tp-cal-td tp-cal-td-meta" :class="{ 'ap-cell-empty': !client.year_end }">{{ formatYearEnd(client.year_end) }}</td>
                  <td v-if="!hiddenMetaGroups.includes('dates')" class="tp-cal-td tp-cal-td-meta ap-mono" :class="{ 'ap-cell-empty': !client.incorporation_date }">{{ client.incorporation_date || '—' }}</td>
                  <td v-if="!hiddenMetaGroups.includes('dates')" class="tp-cal-td tp-cal-td-meta ap-mono">{{ client.cosec_termination_date || '—' }}</td>
                  <td v-if="!hiddenMetaGroups.includes('dates')" class="tp-cal-td tp-cal-td-meta">{{ client.cosec_termination_type || '—' }}</td>
                  <td
                    v-for="m in 12"
                    :key="'td-' + client.id + '-' + m"
                    class="tp-cal-td tp-cal-td-month"
                    :class="{ 'tp-cal-td-current': m === currentMonth && selectedSlot === 'current' }"
                  >
                    <div
                      v-for="dl in getDeadlines(client, m)"
                      :key="dl.is_adhoc ? 'adhoc-' + dl.id : dl.name + (dl._month != null ? '-' + dl._month : '')"
                      class="tp-cal-deadline"
                      :class="deadlineClass(dl, m)"
                    >
                      <!-- <span v-if="dl.date && dl.date.day && !isMonthEnd(m, dl.date.day)" class="tp-cal-dl-day">{{ dl.date.day }}</span> -->
                      <span class="tp-cal-dl-day">{{ dl.date.day }}</span>
                      <span class="tp-cal-dl-label">{{ dl.display_name }}</span>
                      <span v-if="showStatus(dl, m)" class="tp-cal-dl-status" :class="{ 'tp-cal-dl-status--overdue': isOverduePending(dl, m) }">
                        {{ dl.status === 'completed' ? 'Completed' : 'Pending' }}
                      </span>
                      <fa-icon
                        v-if="dl.is_adhoc ? updatingAdhoc[dl.id] : updatingTask[client.id + '-' + dl.name + (dl.is_all_months ? '-' + dl._month : '')]"
                        :icon="['fas', 'spinner']"
                        spin
                        class="tp-dl-status-spinner"
                      />
                      <div
                        v-else
                        class="tp-dl-overlay-trigger"
                        @click.stop="openDlDropdown(client, dl, $event)"
                      ></div>
                    </div>
                  </td>
                  <td class="tp-cal-td tp-cal-td-actions">
                    <div class="ap-row-actions">
                      <app-tooltip text="view" position="left">
                        <button class="ap-row-action ap-row-action-view" title="View" @click="viewingClient = client; showViewModal = true">
                          <fa-icon :icon="['fas', 'eye']" />
                        </button>
                      </app-tooltip>
                      <app-tooltip text="edit" position="left">
                        <button class="ap-row-action ap-row-action-edit" title="Edit" @click="$router.push({ name: 'Admin-Edit-Client', params: { lang: $route.params.lang, id: client.id } })">
                          <fa-icon :icon="['fas', 'edit']" />
                        </button>
                      </app-tooltip>
                    </div>
                  </td>
                  <td class="tp-col-spacer" :style="{ minWidth: spacerWidth + 'px' }"></td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Pagination -->
          <div v-if="totalPages > 1" class="tp-pagination">
            <button class="tp-page-btn" :disabled="currentPage === 1" @click="currentPage--">
              <fa-icon :icon="['fas', 'chevron-left']" />
            </button>
            <span class="tp-page-info">Page {{ currentPage }} / {{ totalPages }}</span>
            <button class="tp-page-btn" :disabled="currentPage === totalPages" @click="currentPage++">
              <fa-icon :icon="['fas', 'chevron-right']" />
            </button>
          </div>
        </template>
      </div>

    </main>

    <client-details-modal
      :show="showViewModal"
      :client="viewingClient"
      :show-documents="false"
      @close="showViewModal = false"
    />

    <!-- Ad hoc deadline form -->
    <ad-hoc-deadline-form
      :show="showAdHocForm"
      :clients="cosecClients"
      context="cosec"
      @close="showAdHocForm = false"
      @saved="onAdHocSaved"
    />

    <!-- Delete ad hoc confirmation -->
    <confirm-modal
      :show="adhocDeleteModal.show"
      title="Delete Ad Hoc Deadline"
      confirm-label="Delete"
      cancel-label="Cancel"
      loading-label="Deleting…"
      :icon="['fas', 'trash']"
      :danger="true"
      :loading="adhocDeleteModal.deleting"
      @cancel="adhocDeleteModal.show = false"
      @confirm="deleteAdhoc"
    >
      Remove "<strong>{{ adhocDeleteModal.item && adhocDeleteModal.item.display_name }}</strong>"
      from <strong>{{ adhocDeleteModal.item && adhocDeleteModal.item.clientName }}</strong>? This cannot be undone.
    </confirm-modal>

    <confirm-modal
      :show="showRolloverConfirm"
      title="Run Year Rollover?"
      confirm-label="Run Rollover"
      cancel-label="Cancel"
      loading-label="Rolling over…"
      :icon="['fas', 'undo-alt']"
      :danger="true"
      :loading="migrating"
      @cancel="showRolloverConfirm = false"
      @confirm="showRolloverConfirm = false; migrateYear(true)"
    >
      This will archive current year records, promote next year to current, and reset pending statuses.
      <strong>This cannot be undone.</strong>
    </confirm-modal>

    <confirm-modal
      :show="showRolloverReminder"
      title="Year Rollover Detected"
      confirm-label="Run Rollover"
      cancel-label="Dismiss"
      loading-label="Rolling over…"
      :icon="['fas', 'sync-alt']"
      :danger="false"
      :loading="migrating"
      @cancel="showRolloverReminder = false"
      @confirm="migrateYear(true)"
    >
      Today is <strong>{{ serverDateFormatted }}</strong> — a new cosec year has been detected.
      Running the rollover will archive {{ serverYear - 1 }} records, promote next year to current, and reset pending statuses.
      Click <em>Dismiss</em> to be reminded again on your next visit.
    </confirm-modal>

    <confirm-modal
      :show="showLogoutModal"
      title="Confirm Logout"
      confirm-label="Logout"
      :icon="['fas', 'sign-out-alt']"
      @cancel="closeLogoutModal"
      @confirm="confirmLogout"
    >Are you sure you want to logout?</confirm-modal>

    <session-modal :show="showAdminSessionModal" @continue="continueAdminSession" @signout="signOutAdminSession">
      Your admin session has expired. Continue to refresh your session or sign out.
    </session-modal>

    <!--
      Portalled deadline status dropdown — rendered here (Vue reactivity),
      moved to <body> in mounted() so position:fixed is always viewport-relative.
      One shared instance repositioned per chip click.
    -->
    <div
      ref="tpDlMenu"
      class="ap-si-menu tp-dl-dropdown-menu"
      v-show="!!openDropdownKey"
      :style="dropdownStyle"
      @click.stop
    >
      <div
        class="ap-si-opt"
        :class="{ 'is-active': openDropdownDl && openDropdownDl.status !== 'completed' }"
        @click="selectDlStatus('pending')"
      >
        <span class="ap-si-opt-label">Pending</span>
        <fa-icon
          v-if="openDropdownDl && openDropdownDl.status !== 'completed'"
          :icon="['fas', 'check']"
          class="ap-si-opt-check"
        />
      </div>
      <div
        class="ap-si-opt"
        :class="{ 'is-active': openDropdownDl && openDropdownDl.status === 'completed' }"
        @click="selectDlStatus('completed')"
      >
        <span class="ap-si-opt-label">Completed</span>
        <fa-icon
          v-if="openDropdownDl && openDropdownDl.status === 'completed'"
          :icon="['fas', 'check']"
          class="ap-si-opt-check"
        />
      </div>
    </div>

  </div>
</template>

<script>
import adminSessionMixin from '@/mixins/adminSessionMixin'
import AdminHeader from '@/components/admin/AdminHeader.vue'
import SessionModal from '@/components/shared/SessionModal.vue'
import ConfirmModal from '@/components/shared/ConfirmModal.vue'
import ClientDetailsModal from '@/components/shared/ClientDetailsModal.vue'
import TaskCalendarView from '@/components/shared/TaskCalendarView.vue'
import AdHocDeadlineForm from '@/components/shared/AdHocDeadlineForm.vue'
import AppBadge from '@/components/shared/AppBadge.vue'
import AppButton from '@/components/shared/AppButton.vue'
import AppDivider from '@/components/shared/AppDivider.vue'
import AppTooltip from '@/components/shared/AppTooltip.vue'
import { lockBodyScroll, unlockBodyScroll } from '@/utils/bodyScrollLock'

let _rolloverChecked = false
let _rolloverNeeded = false

export default {
  name: 'admin-task-cosec',
  mixins: [adminSessionMixin],
  components: { AdminHeader, SessionModal, ConfirmModal, ClientDetailsModal, TaskCalendarView, AdHocDeadlineForm, AppBadge, AppButton, AppDivider, AppTooltip },
  data () {
    const now = new Date()
    return {
      cosecCache: { prev: null, current: null, next: null },
      loading: false,
      migrating: false,
      showRolloverReminder: false,
      showRolloverConfirm: false,
      rolloverNeeded: false,
      serverYear: null,
      serverDateFormatted: '',
      updatingTask: {},
      updatingAdhoc: {},
      selectedSlot: 'current',
      viewMode: 'table',
      calendarMonth: now.getMonth() + 1,
      monthNames: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
      currentMonth: now.getMonth() + 1,
      currentYear: now.getFullYear(),
      pillStyle: {},
      currentPage: 1,
      pageSize: 20,
      tableHeight: 'auto',
      spacerWidth: 0,
      hiddenMetaGroups: [],
      viewingClient: null,
      showViewModal: false,
      showAdHocForm: false,
      adhocStripOpen: false,
      adhocDeleteModal: { show: false, deleting: false, item: null },
      // Deadline status custom dropdown
      openDropdownKey: null,
      openDropdownClient: null,
      openDropdownDl: null,
      dropdownStyle: {}
    }
  },
  computed: {
    cosecClients () {
      return this.cosecCache[this.selectedSlot] || []
    },
    calendarYear () {
      if (this.selectedSlot === 'prev') return this.currentYear - 1
      if (this.selectedSlot === 'next') return this.currentYear + 1
      return this.currentYear
    },
    slotOptions () {
      const y = this.currentYear
      return [
        { slot: 'prev', year: y - 1 },
        { slot: 'current', year: y },
        { slot: 'next', year: y + 1 }
      ]
    },
    totalDeadlines () {
      return this.cosecClients.reduce((sum, c) => sum + (c.items ? c.items.length : 0), 0)
    },
    currentMonthDeadlines () {
      return this.cosecClients.reduce((sum, c) => sum + this.getDeadlines(c, this.currentMonth).length, 0)
    },
    nextMonthDeadlines () {
      const next = this.currentMonth === 12 ? 1 : this.currentMonth + 1
      return this.cosecClients.reduce((sum, c) => sum + this.getDeadlines(c, next).length, 0)
    },
    totalPages () {
      return Math.max(1, Math.ceil(this.cosecClients.length / this.pageSize))
    },
    paginatedClients () {
      const start = (this.currentPage - 1) * this.pageSize
      return this.cosecClients.slice(start, start + this.pageSize)
    },
    adhocStripItems () {
      const enrich = (item, client) => ({ ...item, adhocId: item.id, clientId: client.id, clientName: client.client, initial: client.initial || client.client.slice(0, 4) })
      if (this.viewMode === 'calendar') {
        const m = this.calendarMonth
        const items = []
        for (const client of this.cosecClients) {
          for (const item of (client.adhoc || [])) {
            if (item.date && item.date.month === m) items.push(enrich(item, client))
          }
        }
        return items
      }
      const items = []
      for (const client of this.cosecClients) {
        for (const item of (client.adhoc || [])) items.push(enrich(item, client))
      }
      return items
    },
    adhocStripGroups () {
      const map = {}
      for (const item of this.adhocStripItems) {
        const m = item.date && item.date.month
        if (!m) continue
        if (!map[m]) map[m] = []
        map[m].push(item)
      }
      return Object.keys(map).map(Number).sort((a, b) => a - b)
        .map(m => ({ month: m, monthName: this.monthNames[m - 1], items: map[m] }))
    }
  },
  watch: {
    selectedSlot (slot) {
      this.currentPage = 1
      if (this.cosecCache[slot] === null) this.fetchSlot(slot)
      this.$nextTick(this.updatePill)
    },
    viewMode (val) {
      if (val === 'table') this.$nextTick(this.computeTableHeight)
    },
    showRolloverReminder () {
      this.$nextTick(this.computeTableHeight)
    },
    showAdHocForm (val) {
      val ? lockBodyScroll() : unlockBodyScroll()
    }
  },
  mounted () {
    this.startSessionTimer()
    this.loadColSettings()
    this.fetchSlot('current')
    this.checkRolloverReminder()
    window.addEventListener('resize', this.computeTableHeight)
    window.addEventListener('resize', this.updatePill)
    window.addEventListener('resize', this.computeSpacerWidth)
    this.$nextTick(this.updatePill)
    this.$nextTick(this.computeSpacerWidth)
    // Portal: move deadline dropdown to <body> so position:fixed is viewport-relative
    document.body.appendChild(this.$refs.tpDlMenu)
    document.addEventListener('click', this.onDlOutsideClick)
    window.addEventListener('scroll', this.onDlScroll, true)
    window.addEventListener('resize', this.closeDlDropdown)
  },
  beforeDestroy () {
    window.removeEventListener('resize', this.computeTableHeight)
    window.removeEventListener('resize', this.updatePill)
    window.removeEventListener('resize', this.computeSpacerWidth)
    window.removeEventListener('resize', this.closeDlDropdown)
    if (this.showAdHocForm) unlockBodyScroll()
    document.removeEventListener('click', this.onDlOutsideClick)
    window.removeEventListener('scroll', this.onDlScroll, true)
    const menu = this.$refs.tpDlMenu
    if (menu && menu.parentNode === document.body) {
      document.body.removeChild(menu)
    }
  },
  methods: {
    // ── Column visibility persistence ─────────────────────────────────────────
    async loadColSettings () {
      try {
        const data = await this.adminFetch('/api/admin/settings/get.php')
        if (data && data.success && Array.isArray(data.data.cosec_col_visibility)) {
          this.hiddenMetaGroups = data.data.cosec_col_visibility
        }
      } catch (err) { /* silent — keep defaults */ }
    },
    saveColSettings () {
      this.adminFetch('/api/admin/settings/save.php', {
        method: 'POST',
        body: JSON.stringify({ cosec_col_visibility: this.hiddenMetaGroups })
      }).catch(() => {})
    },
    // ─────────────────────────────────────────────────────────────────────────
    updatePill () {
      const container = this.$refs.yearTabs
      if (!container) return
      const active = container.querySelector('.tp-year-tab.is-active')
      if (!active) return
      const cr = container.getBoundingClientRect()
      const ar = active.getBoundingClientRect()
      this.pillStyle = { left: `${ar.left - cr.left}px`, width: `${ar.width}px` }
    },
    prevCalMonth () { if (this.calendarMonth > 1) this.calendarMonth-- },
    nextCalMonth () { if (this.calendarMonth < 12) this.calendarMonth++ },
    setCalMonth (m) { this.calendarMonth = m },
    rowNumber (i) {
      return (this.currentPage - 1) * this.pageSize + i + 1
    },
    async checkRolloverReminder () {
      if (_rolloverChecked) {
        this.rolloverNeeded = _rolloverNeeded
        this.showRolloverReminder = _rolloverNeeded
        return
      }
      try {
        const data = await this.adminFetch('/api/admin/tasks/server-date.php?type=cosec')
        if (data && data.success) {
          _rolloverChecked = true
          _rolloverNeeded = data.need_rollover === true
          this.rolloverNeeded = _rolloverNeeded
          this.serverYear = data.year
          this.serverDateFormatted = `${data.day} ${this.monthNames[data.month - 1]} ${data.year}`
          this.showRolloverReminder = _rolloverNeeded
        }
      } catch (err) {
        console.error('Failed to fetch server date:', err)
      }
    },
    computeTableHeight () {
      if (!this.$refs.calWrap) return
      this.tableHeight = 'auto'
      this.$nextTick(() => {
        const wrap = this.$refs.calWrap
        if (!wrap) return
        const h = wrap.scrollHeight
        this.tableHeight = `${h < 600 ? h + 15 : 600}px`
        setTimeout(() => {
          this.computeSpacerWidth()
        }, 100)
      })
    },
    toggleMetaGroup (key) {
      const idx = this.hiddenMetaGroups.indexOf(key)
      if (idx === -1) this.hiddenMetaGroups.push(key)
      else this.hiddenMetaGroups.splice(idx, 1)
      this.$nextTick(this.computeSpacerWidth)
      this.saveColSettings()
    },
    computeSpacerWidth () {
      const wrap = this.$refs.calWrap
      if (!wrap) return
      // Measure sticky columns (#, Client) from DOM
      const numEl = wrap.querySelector('.tp-cal-th-num')
      const clientEl = wrap.querySelector('.tp-cal-th-company')
      const numW = numEl ? numEl.offsetWidth : 50
      const clientW = clientEl ? clientEl.offsetWidth : 200
      // Measure last month (Dec) and actions column from DOM
      const monthThs = wrap.querySelectorAll('.tp-cal-th-month')
      const decEl = monthThs.length ? monthThs[monthThs.length - 1] : null
      const actionsEl = wrap.querySelector('.tp-cal-td-actions')
      const decW = decEl ? decEl.offsetWidth : 90
      console.log('decW ==', decW)
      const actionsW = actionsEl ? actionsEl.offsetWidth : 80
      // Spacer = remaining visible width so Dec+Actions land flush against sticky cols
      this.spacerWidth = Math.max(0, wrap.clientWidth - numW - clientW - decW - actionsW)
    },
    async fetchSlot (slot) {
      this.loading = true
      try {
        const data = await this.adminFetch(`/api/admin/tasks/cosec-clients.php?slot=${slot}`)
        if (data === null) { this.pendingAdminRetry = () => this.fetchSlot(slot); return }
        if (data && data.success) {
          this.$set(this.cosecCache, slot, data.data || [])
          this.$nextTick(this.computeTableHeight)
        }
      } catch (err) {
        console.error('Failed to load cosec clients:', err)
      } finally {
        this.loading = false
      }
    },
    getDeadlines (client, month) {
      const regular = (client.items || [])
        .filter(item => {
          if (!item.date) return false
          if (item.date.month) return item.date.month === month
          return item.date.day != null
        })
        .map(item => {
          if (!item.is_all_months) return item
          const status = (item.month_statuses && item.month_statuses[String(month)]) || 'pending'
          return { ...item, status, _month: month }
        })
      const adhoc = (client.adhoc || []).filter(item => item.date && item.date.month === month)
      return [...regular, ...adhoc].sort((a, b) => {
        const da = (a.date && a.date.day) || Infinity
        const db = (b.date && b.date.day) || Infinity
        return da - db
      })
    },
    showStatus (dl, month) {
      if (this.selectedSlot === 'prev') return true
      if (this.selectedSlot === 'current' && month <= this.currentMonth) return true
      return dl.status === 'completed'
    },
    isOverduePending (dl, month) {
      if (dl.status !== 'pending') return false
      if (this.selectedSlot === 'prev') return true
      if (this.selectedSlot === 'current') return month < this.currentMonth
      return false
    },
    isMonthEnd (month, day) {
      const lastDays = [0, 31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31]
      return day === lastDays[month]
    },
    deadlineClass (dl, month) {
      if (dl.is_adhoc) {
        if (dl.status === 'completed') return 'tp-dl--adhoc tp-dl--adhoc-done'
        const isDue = this.selectedSlot === 'prev' || (this.selectedSlot === 'current' && month <= this.currentMonth)
        return isDue ? 'tp-dl--adhoc tp-dl--adhoc-due' : 'tp-dl--adhoc'
      }
      if (dl.status === 'completed') return 'tp-dl--grey'
      if (this.selectedSlot !== 'current' || month !== this.currentMonth) return 'tp-dl--grey'
      const group = dl.group || ''
      if (group === 'compliance') return 'tp-dl--cosec-comp'
      if (group === 'reminder') return 'tp-dl--cosec-reminder'
      if (group === 'payroll_monthly' || group === 'payroll_yearly' || group === 'payroll_reminder') return 'tp-dl--payroll'
      if (group === 'accounting') return 'tp-dl--accounting'
      return 'tp-dl--grey'
    },
    formatClientType (val) {
      const map = { sdn_bhd: 'Sdn Bhd', llp: 'LLP', sole_prop: 'Sole Prop', partnership: 'Partnership', salaried_person: 'Salaried' }
      return map[val] || val || '—'
    },
    formatYearEnd (val) {
      return this.monthNames[(val || 1) - 1] || '—'
    },
    async updateTaskStatus (client, dl, newStatus) {
      if (dl.is_adhoc) { return this.updateAdhocStatus(client, dl, newStatus) }
      const key = `${client.id}-${dl.name}${dl.is_all_months ? '-' + dl._month : ''}`
      this.$set(this.updatingTask, key, true)
      try {
        const payload = { client_id: client.id, task_name: dl.name, slot: this.selectedSlot, status: newStatus }
        if (dl.is_all_months) payload.month = dl._month
        const data = await this.adminFetch('/api/admin/tasks/update-cosec-status.php', {
          method: 'POST',
          body: JSON.stringify(payload)
        })
        if (data === null) { this.pendingAdminRetry = () => this.updateTaskStatus(client, dl, newStatus); return }
        if (data && data.success) {
          if (dl.is_all_months) {
            const srcItem = client.items.find(i => i.name === dl.name && i.is_all_months)
            if (srcItem) {
              if (!srcItem.month_statuses) this.$set(srcItem, 'month_statuses', {})
              this.$set(srcItem.month_statuses, String(dl._month), newStatus)
            }
          } else {
            this.$set(dl, 'status', newStatus)
          }
        }
      } catch (err) {
        console.error('Failed to update cosec task status:', err)
      } finally {
        this.$set(this.updatingTask, key, false)
      }
    },
    async updateAdhocStatus (client, dl, newStatus) {
      this.$set(this.updatingAdhoc, dl.id, true)
      try {
        const data = await this.adminFetch('/api/admin/tasks/update-adhoc-status.php', {
          method: 'POST',
          body: JSON.stringify({ id: dl.id, status: newStatus })
        })
        if (data === null) { this.pendingAdminRetry = () => this.updateAdhocStatus(client, dl, newStatus); return }
        if (data && data.success) this.$set(dl, 'status', newStatus)
      } catch (err) {
        console.error('Failed to update ad hoc status:', err)
      } finally {
        this.$set(this.updatingAdhoc, dl.id, false)
      }
    },
    // ── Deadline status custom dropdown (portal) ─────────────────────────────
    openDlDropdown (client, dl, e) {
      const key = dl.is_adhoc ? 'adhoc-' + dl.id : client.id + '-' + dl.name + (dl.is_all_months ? '-' + dl._month : '')
      if (this.openDropdownKey === key) { this.closeDlDropdown(); return }
      this.openDropdownKey = key
      this.openDropdownClient = client
      this.openDropdownDl = dl
      this._dlTriggerEl = e.currentTarget
      this.$nextTick(this.positionDlDropdown)
    },
    positionDlDropdown () {
      if (!this._dlTriggerEl) return
      const r = this._dlTriggerEl.getBoundingClientRect()
      this.dropdownStyle = { position: 'fixed', top: (r.bottom + 4) + 'px', left: r.left + 'px', zIndex: 10000 }
    },
    closeDlDropdown () {
      this.openDropdownKey = null
      this.openDropdownClient = null
      this.openDropdownDl = null
      this._dlTriggerEl = null
    },
    onDlOutsideClick (e) {
      const menu = this.$refs.tpDlMenu
      if (menu && menu.contains(e.target)) return
      if (this.openDropdownKey) this.closeDlDropdown()
    },
    onDlScroll () {
      if (this.openDropdownKey) this.positionDlDropdown()
    },
    async selectDlStatus (status) {
      if (!this.openDropdownClient || !this.openDropdownDl) return
      const client = this.openDropdownClient
      const dl = this.openDropdownDl
      this.closeDlDropdown()
      await this.updateTaskStatus(client, dl, status)
    },
    // ─────────────────────────────────────────────────────────────────────────
    onAdHocSaved ({ clientId, item }) {
      const slotYears = { prev: this.currentYear - 1, current: this.currentYear, next: this.currentYear + 1 }
      for (const slot of ['prev', 'current', 'next']) {
        if (slotYears[slot] !== item.year) continue
        const clients = this.cosecCache[slot]
        if (!clients) continue
        const client = clients.find(c => c.id === clientId)
        if (client) {
          if (!client.adhoc) this.$set(client, 'adhoc', [])
          client.adhoc.push(item)
        }
      }
    },
    confirmDeleteAdhoc (item) {
      this.adhocDeleteModal = { show: true, deleting: false, item }
    },
    async deleteAdhoc () {
      const item = this.adhocDeleteModal.item
      if (!item) return
      this.adhocDeleteModal.deleting = true
      try {
        const data = await this.adminFetch('/api/admin/tasks/delete-adhoc-deadline.php', {
          method: 'POST',
          body: JSON.stringify({ id: item.adhocId, client_id: item.clientId })
        })
        if (data === null) { this.pendingAdminRetry = () => this.deleteAdhoc(); return }
        if (data && data.success) {
          for (const slot of ['prev', 'current', 'next']) {
            const clients = this.cosecCache[slot]
            if (!clients) continue
            const client = clients.find(c => c.id === item.clientId)
            if (client && client.adhoc) {
              const idx = client.adhoc.findIndex(a => a.id === item.adhocId)
              if (idx !== -1) client.adhoc.splice(idx, 1)
            }
          }
          this.adhocDeleteModal.show = false
        }
      } catch (err) {
        console.error('Failed to delete ad hoc deadline:', err)
      } finally {
        this.adhocDeleteModal.deleting = false
      }
    },
    async migrateYear (skipConfirm = false) {
      this.migrating = true
      try {
        const data = await this.adminFetch('/api/admin/tasks/migrate-cosec-year.php', { method: 'POST' })
        if (data === null) { this.pendingAdminRetry = () => this.migrateYear(skipConfirm); return }
        if (data && data.success) {
          _rolloverNeeded = false
          this.rolloverNeeded = false
          this.showRolloverReminder = false
          this.$set(this, 'cosecCache', { prev: null, current: null, next: null })
          this.selectedSlot = 'current'
          this.currentPage = 1
          await this.fetchSlot('current')
        }
      } catch (err) {
        console.error('Migration failed:', err)
      } finally {
        this.migrating = false
      }
    }
  }
}
</script>
